import * as React from 'react'
import { X, Sparkles, Send, Loader2, Clock, MapPin, User, Car } from 'lucide-react'
import { parseOrders } from '@/api/orders'
import type { AIParseResponse, AIParseResult } from '@/types'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

interface AIOrderDrawerProps {
  isOpen: boolean
  onClose: () => void
  onSingleOrder: (order: any, rawText: string) => void
  onMultipleOrders: (orders: any[], rawText: string) => void
}

// Example prompts for users
const EXAMPLE_PROMPTS = [
  {
    icon: MapPin,
    title: '單筆訂單',
    text: '明天上午10點，陳先生，0912345678，從台北車站送到桃園機場一航廈，3人，2件行李',
  },
  {
    icon: Clock,
    title: '指定時間',
    text: '12/25 下午2點接機，李女士，0987654321，從桃園機場二航廈到台北君悅酒店，需要兒童座椅',
  },
  {
    icon: User,
    title: '多筆訂單',
    text: '明天10點陳先生送機到桃園機場，還有下午3點李女士從桃園機場接機到台北車站，另外晚上8點王先生從台北101送到松山機場',
  },
]

export default function AIOrderDrawer({
  isOpen,
  onClose,
  onSingleOrder,
  onMultipleOrders,
}: AIOrderDrawerProps) {
  const [input, setInput] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  // Focus textarea when drawer opens
  React.useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  // Reset state when drawer closes
  React.useEffect(() => {
    if (!isOpen) {
      setInput('')
      setError(null)
    }
  }, [isOpen])

  const handleExampleClick = (exampleText: string) => {
    setInput(exampleText)
    textareaRef.current?.focus()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!input.trim()) {
      setError('請輸入訂單資訊')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response: AIParseResponse = await parseOrders(input)

      if (!response.success || !response.data) {
        setError(response.error || '解析失敗，請重新輸入')
        return
      }

      const result = response.data

      // Route based on order type
      if (result.type === 'single' && result.orders.length === 1) {
        // Single order - open OrderDrawer with pre-filled data
        onSingleOrder(result.orders[0], result.raw_text || input)
      } else {
        // Multiple orders - open BulkOrdersConfirmDrawer
        onMultipleOrders(result.orders, result.raw_text || input)
      }

      // Close AI drawer
      onClose()
    } catch (err: any) {
      console.error('AI parsing error:', err)
      setError(err.response?.data?.detail || err.message || '解析失敗，請稍後再試')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[500px] bg-white dark:bg-gray-800 shadow-xl z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                AI 智能建單
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                用自然語言描述訂單，AI 自動解析
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Input Area */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Textarea
                ref={textareaRef}
                placeholder="請輸入訂單資訊，例如：&#10;&#10;明天上午10點，陳先生，0912345678，從台北車站送到桃園機場，3人，2件行李"
                className="min-h-[180px] text-base bg-gray-50 dark:bg-gray-900/50 border-2 border-dashed border-gray-200 dark:border-gray-700 focus:border-orange-400 dark:focus:border-orange-600 resize-none"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value)
                  if (error) setError(null)
                }}
                disabled={isLoading}
              />

              {/* Character count */}
              <div className="flex justify-end mt-1">
                <span className={cn(
                  'text-xs',
                  input.length > 4000 ? 'text-red-500' : 'text-gray-400'
                )}>
                  {input.length} / 5000
                </span>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-base shadow-lg disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  AI 解析中...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  開始解析
                </>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 bg-white dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400 font-medium">
                點擊範例快速填入
              </span>
            </div>
          </div>

          {/* Example Prompts */}
          <div className="space-y-2">
            {EXAMPLE_PROMPTS.map((example, index) => {
              const Icon = example.icon
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleExampleClick(example.text)}
                  disabled={isLoading}
                  className="w-full p-3 text-left bg-gray-50 dark:bg-gray-900/50 hover:bg-orange-50 dark:hover:bg-orange-900/20 border border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-700 rounded-lg transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30 transition-colors">
                      <Icon className="w-4 h-4 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                        {example.title}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                        {example.text}
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          {/* Tips */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
              支援單筆與多筆訂單輸入，多筆訂單可使用「還有」、「另外」等詞區隔
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
